import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  Shield, 
  Check, 
  X, 
  Clock, 
  Package, 
  MessageSquare,
  Eye,
  User,
  AlertTriangle,
  RefreshCw,
  ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminModeration = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isFr = i18n.language === 'fr';
  
  const [stats, setStats] = useState(null);
  const [pendingListings, setPendingListings] = useState([]);
  const [pendingPosts, setPendingPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('listings');
  const [expandedItems, setExpandedItems] = useState({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, listingsRes, postsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/stats`),
        axios.get(`${API_URL}/admin/pending/listings`),
        axios.get(`${API_URL}/admin/pending/posts`)
      ]);
      
      setStats(statsRes.data);
      setPendingListings(listingsRes.data);
      setPendingPosts(postsRes.data);
    } catch (error) {
      console.error('Error fetching moderation data:', error);
      if (error.response?.status === 403) {
        toast.error(isFr ? 'Accès administrateur requis' : 'Admin access required');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [isFr, navigate]);

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error(isFr ? 'Accès administrateur requis' : 'Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate, isFr, fetchData]);

  const handleModerate = async (type, id, action, note = null) => {
    try {
      const endpoint = type === 'listing' 
        ? `${API_URL}/admin/moderate/listing/${id}`
        : `${API_URL}/admin/moderate/post/${id}`;
      
      await axios.post(endpoint, { action, note });
      
      toast.success(
        action === 'approve' 
          ? (isFr ? 'Contenu approuvé' : 'Content approved')
          : (isFr ? 'Contenu rejeté' : 'Content rejected')
      );
      
      // Refresh data
      fetchData();
    } catch (error) {
      console.error('Moderation error:', error);
      toast.error(isFr ? 'Erreur lors de la modération' : 'Moderation error');
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-moderation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl uppercase tracking-tight">
              {isFr ? 'Modération' : 'Moderation'}
            </h1>
            <p className="text-muted-foreground text-sm">
              {isFr ? 'Validez le contenu avant publication' : 'Validate content before publication'}
            </p>
          </div>
        </div>
        
        <Button 
          variant="outline" 
          onClick={fetchData}
          data-testid="refresh-moderation"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          {isFr ? 'Actualiser' : 'Refresh'}
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-yellow-500/20 bg-yellow-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Package className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold text-yellow-500">{stats.pending_listings}</p>
                  <p className="text-xs text-muted-foreground">
                    {isFr ? 'Annonces en attente' : 'Pending listings'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-yellow-500/20 bg-yellow-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold text-yellow-500">{stats.pending_posts}</p>
                  <p className="text-xs text-muted-foreground">
                    {isFr ? 'Posts en attente' : 'Pending posts'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-green-500/20 bg-green-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Check className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold text-green-500">{stats.approved_today}</p>
                  <p className="text-xs text-muted-foreground">
                    {isFr ? "Approuvés aujourd'hui" : 'Approved today'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-red-500/20 bg-red-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <X className="w-8 h-8 text-red-500" />
                <div>
                  <p className="text-2xl font-bold text-red-500">{stats.rejected_today}</p>
                  <p className="text-xs text-muted-foreground">
                    {isFr ? "Rejetés aujourd'hui" : 'Rejected today'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Moderation Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="listings" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            {isFr ? 'Annonces' : 'Listings'}
            {stats?.pending_listings > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-500 rounded-full">
                {stats.pending_listings}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="posts" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Posts
            {stats?.pending_posts > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-500 rounded-full">
                {stats.pending_posts}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Listings Tab */}
        <TabsContent value="listings" className="space-y-4 mt-6">
          {pendingListings.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {isFr ? 'Aucune annonce en attente de modération' : 'No listings pending moderation'}
                </p>
              </CardContent>
            </Card>
          ) : (
            pendingListings.map((listing) => (
              <Card key={listing.id} className="border-yellow-500/20" data-testid={`listing-${listing.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-yellow-500" />
                        <span className="text-xs text-yellow-500 font-medium">
                          {isFr ? 'En attente' : 'Pending'}
                        </span>
                      </div>
                      <CardTitle className="text-lg">{listing.title}</CardTitle>
                      <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                        <User className="w-3 h-3" />
                        <span>{listing.seller_name}</span>
                        <span>•</span>
                        <span className="text-primary font-semibold">{listing.price} {listing.currency}</span>
                      </div>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpand(listing.id)}
                    >
                      <ChevronDown className={`w-4 h-4 transition-transform ${expandedItems[listing.id] ? 'rotate-180' : ''}`} />
                    </Button>
                  </div>
                </CardHeader>
                
                <CardContent>
                  {expandedItems[listing.id] && (
                    <div className="mb-4 space-y-3">
                      <p className="text-sm text-muted-foreground">{listing.description}</p>
                      
                      {listing.images?.length > 0 && (
                        <div className="flex gap-2 overflow-x-auto pb-2">
                          {listing.images.map((img, idx) => (
                            <img 
                              key={idx}
                              src={img.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`}
                              alt={`${listing.title} ${idx + 1}`}
                              className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                            />
                          ))}
                        </div>
                      )}
                      
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="px-2 py-1 bg-muted rounded">{listing.category}</span>
                        <span className="px-2 py-1 bg-muted rounded">{listing.condition}</span>
                        <span className="px-2 py-1 bg-muted rounded">{listing.location}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleModerate('listing', listing.id, 'approve')}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      data-testid={`approve-listing-${listing.id}`}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {isFr ? 'Approuver' : 'Approve'}
                    </Button>
                    <Button
                      onClick={() => handleModerate('listing', listing.id, 'reject')}
                      variant="destructive"
                      className="flex-1"
                      data-testid={`reject-listing-${listing.id}`}
                    >
                      <X className="w-4 h-4 mr-2" />
                      {isFr ? 'Rejeter' : 'Reject'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => window.open(`/marketplace/${listing.id}`, '_blank')}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Posts Tab */}
        <TabsContent value="posts" className="space-y-4 mt-6">
          {pendingPosts.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {isFr ? 'Aucun post en attente de modération' : 'No posts pending moderation'}
                </p>
              </CardContent>
            </Card>
          ) : (
            pendingPosts.map((post) => (
              <Card key={post.id} className="border-yellow-500/20" data-testid={`post-${post.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        {post.avatar_url ? (
                          <img 
                            src={post.avatar_url.startsWith('http') ? post.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${post.avatar_url}`}
                            alt={post.username}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <User className="w-5 h-5 text-primary" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3 text-yellow-500" />
                          <span className="text-xs text-yellow-500 font-medium">
                            {isFr ? 'En attente' : 'Pending'}
                          </span>
                        </div>
                        <p className="font-medium">{post.username}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(post.created_at).toLocaleDateString(isFr ? 'fr-FR' : 'en-US')}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <p className="text-sm mb-4">{post.content}</p>
                  
                  {post.images?.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
                      {post.images.map((img, idx) => (
                        <img 
                          key={idx}
                          src={img.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`}
                          alt={`Post image ${idx + 1}`}
                          className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                        />
                      ))}
                    </div>
                  )}
                  
                  {post.location && (
                    <p className="text-xs text-muted-foreground mb-4">
                      📍 {post.location}
                    </p>
                  )}
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleModerate('post', post.id, 'approve')}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      data-testid={`approve-post-${post.id}`}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {isFr ? 'Approuver' : 'Approve'}
                    </Button>
                    <Button
                      onClick={() => handleModerate('post', post.id, 'reject')}
                      variant="destructive"
                      className="flex-1"
                      data-testid={`reject-post-${post.id}`}
                    >
                      <X className="w-4 h-4 mr-2" />
                      {isFr ? 'Rejeter' : 'Reject'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Help text */}
      <Card className="border-muted">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">
              {isFr ? 'Guide de modération' : 'Moderation Guide'}
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>{isFr ? 'Vérifiez que le contenu respecte nos CGU' : 'Verify content complies with our ToS'}</li>
              <li>{isFr ? 'Pas de contenu illégal, haineux ou spam' : 'No illegal, hateful content or spam'}</li>
              <li>{isFr ? 'Les annonces doivent avoir des prix réalistes' : 'Listings must have realistic prices'}</li>
              <li>{isFr ? 'Les images doivent être appropriées' : 'Images must be appropriate'}</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminModeration;
